/**
 * Perplexity API Type Definitions
 * Based on Perplexity Sonar API documentation
 */

/**
 * Available Perplexity models
 */
export type PerplexityModel = 'sonar' | 'sonar-pro' | 'sonar-reasoning' | 'sonar-reasoning-pro';

/**
 * Search recency filter options
 */
export type SearchRecencyFilter = 'hour' | 'day' | 'week' | 'month' | 'year';

/**
 * Message role in conversation
 */
export type MessageRole = 'system' | 'user' | 'assistant';

/**
 * Individual message in the conversation
 */
export interface PerplexityMessage {
  role: MessageRole;
  content: string;
}

/**
 * Response format for structured outputs
 */
export interface ResponseFormat {
  type: 'json_schema';
  json_schema: {
    name?: string;
    schema: Record<string, any>;
    strict?: boolean;
  };
}

/**
 * Perplexity API request payload
 */
export interface PerplexityRequest {
  model: PerplexityModel;
  messages: PerplexityMessage[];
  temperature?: number; // 0.0 - 2.0, default 0.2
  max_tokens?: number; // default 1000
  stream?: boolean; // default false
  search_recency_filter?: SearchRecencyFilter;
  return_images?: boolean; // Available from Tier 2
  return_related_questions?: boolean; // Available from Tier 2
  search_domain_filter?: string[]; // Filter to specific domains (max 20), prefix with - to exclude
  response_format?: ResponseFormat; // Structured outputs with JSON Schema
}

/**
 * Token usage information
 */
export interface PerplexityUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

/**
 * Message delta for streaming responses
 */
export interface PerplexityDelta {
  role: 'assistant';
  content: string;
}

/**
 * Individual choice in the response
 */
export interface PerplexityChoice {
  index: number;
  finish_reason: 'stop' | 'length';
  message: {
    role: 'assistant';
    content: string;
  };
  delta?: PerplexityDelta;
}

/**
 * Complete Perplexity API response
 */
export interface PerplexityResponse {
  id: string;
  model: string;
  created: number;
  usage: PerplexityUsage;
  citations?: string[]; // URLs of sources used
  object: 'chat.completion';
  choices: PerplexityChoice[];
}

/**
 * Simplified app-level AI insight type
 * Used throughout the application for displaying results
 */
export interface AIInsight {
  content: string; // The AI's response text
  citations: string[]; // Source URLs
  relatedQuestions?: string[]; // Suggested follow-up questions
  model: string; // Model used for generation
  timestamp: number; // When the insight was generated
}

/**
 * Error response from Perplexity API
 */
export interface PerplexityError {
  error: {
    message: string;
    type: string;
    code?: string;
  };
}

/**
 * Service response wrapper
 */
export interface PerplexityServiceResponse<T> {
  data: T | null;
  error: Error | null;
}

/**
 * News article snippet for home screen (structured)
 */
export interface NewsArticle {
  headline: string;
  summary: string;
  source: string;
  url: string;
  timestamp: string;
  impact: 'positive' | 'negative' | 'neutral';
}

/**
 * Stock event for timeline (structured)
 */
export interface StockEvent {
  date: string;
  headline: string;
  description: string;
  source: string;
  url: string;
  event_type: 'earnings' | 'news' | 'analyst' | 'executive' | 'product' | 'other';
  sentiment: 'positive' | 'negative' | 'neutral';
}

/**
 * Structured market news response
 */
export interface StructuredMarketNews {
  market_news: NewsArticle[];
}

/**
 * Structured stock events response
 */
export interface StructuredStockEvents {
  stock_events: StockEvent[];
}

/**
 * JSON Schema for market news
 */
export const MARKET_NEWS_SCHEMA = {
  type: 'object',
  properties: {
    market_news: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          headline: { type: 'string' },
          summary: { type: 'string' },
          source: { type: 'string' },
          url: { type: 'string' },
          timestamp: { type: 'string' },
          impact: { type: 'string', enum: ['positive', 'negative', 'neutral'] },
        },
        required: ['headline', 'summary', 'source', 'impact'],
      },
    },
  },
  required: ['market_news'],
};

/**
 * JSON Schema for stock events
 */
export const STOCK_EVENTS_SCHEMA = {
  type: 'object',
  properties: {
    stock_events: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          date: { type: 'string' },
          headline: { type: 'string' },
          description: { type: 'string' },
          source: { type: 'string' },
          url: { type: 'string' },
          event_type: {
            type: 'string',
            enum: ['earnings', 'news', 'analyst', 'executive', 'product', 'other'],
          },
          sentiment: { type: 'string', enum: ['positive', 'negative', 'neutral'] },
        },
        required: ['date', 'headline', 'description', 'source', 'sentiment', 'event_type'],
      },
    },
  },
  required: ['stock_events'],
};

/**
 * Intelligent Stock Outlook (AI-powered comprehensive analysis)
 * @deprecated Use ComprehensiveStockAnalysis instead
 */
export interface IntelligentOutlook {
  marketStatus: {
    summary: string;
    priceAction: string;
  };
  recentDevelopments: string[];
  technicalOutlook: {
    trend: 'bullish' | 'bearish' | 'neutral';
    keyLevels: {
      support: number | null;
      resistance: number | null;
    };
    summary: string;
  };
  fundamentalPerspective: {
    valuation: string;
    competitive: string;
    outlook: string;
  };
  risksAndCatalysts: {
    catalysts: string[];
    risks: string[];
  };
  lastUpdated: string;
}

/**
 * Comprehensive Stock Analysis (Enhanced financial analyst perspective)
 */
export interface ComprehensiveStockAnalysis {
  companyOverview: {
    businessModel: string;
    industry: string;
    keyProducts: string;
    recentNews: string[];
  };
  marketStatus: {
    summary: string;
    priceAction: string;
    dailyPerformance: string;
  };
  fundamentalAnalysis: {
    keyMetrics: {
      revenue: string;
      eps: string;
      pe: string;
      debtToEquity: string;
      roe: string;
      fcf: string;
    };
    valuation: string;
    peerComparison: string;
  };
  technicalAnalysis: {
    trend: 'bullish' | 'bearish' | 'neutral';
    indicators: {
      rsi: string;
      macd: string;
      movingAverages: string;
    };
    keyLevels: {
      support: number | null;
      resistance: number | null;
    };
    volumeAnalysis: string;
    chartPattern: string;
    summary: string;
  };
  marketSentiment: {
    analystRatings: string;
    priceTargets: string;
    institutionalActivity: string;
    shortInterest: string;
    retailSentiment: string;
  };
  recentDevelopments: string[];
  investmentOutlook: {
    shortTerm: string;
    longTerm: string;
    overallStance: 'bullish' | 'neutral' | 'bearish';
    confidence: string;
  };
  risksAndCatalysts: {
    catalysts: string[];
    risks: string[];
  };
  lastUpdated: string;
}

/**
 * Structured intelligent outlook response
 */
export interface StructuredIntelligentOutlook {
  intelligent_outlook: IntelligentOutlook;
}

/**
 * JSON Schema for intelligent stock outlook
 * @deprecated Use COMPREHENSIVE_ANALYSIS_SCHEMA instead
 */
export const INTELLIGENT_OUTLOOK_SCHEMA = {
  type: 'object',
  properties: {
    intelligent_outlook: {
      type: 'object',
      properties: {
        marketStatus: {
          type: 'object',
          properties: {
            summary: { type: 'string' },
            priceAction: { type: 'string' },
          },
          required: ['summary', 'priceAction'],
        },
        recentDevelopments: {
          type: 'array',
          items: { type: 'string' },
        },
        technicalOutlook: {
          type: 'object',
          properties: {
            trend: { type: 'string', enum: ['bullish', 'bearish', 'neutral'] },
            keyLevels: {
              type: 'object',
              properties: {
                support: { type: ['number', 'null'] },
                resistance: { type: ['number', 'null'] },
              },
              required: ['support', 'resistance'],
            },
            summary: { type: 'string' },
          },
          required: ['trend', 'keyLevels', 'summary'],
        },
        fundamentalPerspective: {
          type: 'object',
          properties: {
            valuation: { type: 'string' },
            competitive: { type: 'string' },
            outlook: { type: 'string' },
          },
          required: ['valuation', 'competitive', 'outlook'],
        },
        risksAndCatalysts: {
          type: 'object',
          properties: {
            catalysts: {
              type: 'array',
              items: { type: 'string' },
            },
            risks: {
              type: 'array',
              items: { type: 'string' },
            },
          },
          required: ['catalysts', 'risks'],
        },
        lastUpdated: { type: 'string' },
      },
      required: [
        'marketStatus',
        'recentDevelopments',
        'technicalOutlook',
        'fundamentalPerspective',
        'risksAndCatalysts',
        'lastUpdated',
      ],
    },
  },
  required: ['intelligent_outlook'],
};

/**
 * JSON Schema for comprehensive stock analysis
 */
export const COMPREHENSIVE_ANALYSIS_SCHEMA = {
  type: 'object',
  properties: {
    comprehensive_analysis: {
      type: 'object',
      properties: {
        companyOverview: {
          type: 'object',
          properties: {
            businessModel: { type: 'string' },
            industry: { type: 'string' },
            keyProducts: { type: 'string' },
            recentNews: {
              type: 'array',
              items: { type: 'string' },
            },
          },
          required: ['businessModel', 'industry', 'keyProducts', 'recentNews'],
        },
        marketStatus: {
          type: 'object',
          properties: {
            summary: { type: 'string' },
            priceAction: { type: 'string' },
            dailyPerformance: { type: 'string' },
          },
          required: ['summary', 'priceAction', 'dailyPerformance'],
        },
        fundamentalAnalysis: {
          type: 'object',
          properties: {
            keyMetrics: {
              type: 'object',
              properties: {
                revenue: { type: 'string' },
                eps: { type: 'string' },
                pe: { type: 'string' },
                debtToEquity: { type: 'string' },
                roe: { type: 'string' },
                fcf: { type: 'string' },
              },
              required: ['revenue', 'eps', 'pe', 'debtToEquity', 'roe', 'fcf'],
            },
            valuation: { type: 'string' },
            peerComparison: { type: 'string' },
          },
          required: ['keyMetrics', 'valuation', 'peerComparison'],
        },
        technicalAnalysis: {
          type: 'object',
          properties: {
            trend: { type: 'string', enum: ['bullish', 'bearish', 'neutral'] },
            indicators: {
              type: 'object',
              properties: {
                rsi: { type: 'string' },
                macd: { type: 'string' },
                movingAverages: { type: 'string' },
              },
              required: ['rsi', 'macd', 'movingAverages'],
            },
            keyLevels: {
              type: 'object',
              properties: {
                support: { type: ['number', 'null'] },
                resistance: { type: ['number', 'null'] },
              },
              required: ['support', 'resistance'],
            },
            volumeAnalysis: { type: 'string' },
            chartPattern: { type: 'string' },
            summary: { type: 'string' },
          },
          required: [
            'trend',
            'indicators',
            'keyLevels',
            'volumeAnalysis',
            'chartPattern',
            'summary',
          ],
        },
        marketSentiment: {
          type: 'object',
          properties: {
            analystRatings: { type: 'string' },
            priceTargets: { type: 'string' },
            institutionalActivity: { type: 'string' },
            shortInterest: { type: 'string' },
            retailSentiment: { type: 'string' },
          },
          required: [
            'analystRatings',
            'priceTargets',
            'institutionalActivity',
            'shortInterest',
            'retailSentiment',
          ],
        },
        recentDevelopments: {
          type: 'array',
          items: { type: 'string' },
        },
        investmentOutlook: {
          type: 'object',
          properties: {
            shortTerm: { type: 'string' },
            longTerm: { type: 'string' },
            overallStance: { type: 'string', enum: ['bullish', 'neutral', 'bearish'] },
            confidence: { type: 'string' },
          },
          required: ['shortTerm', 'longTerm', 'overallStance', 'confidence'],
        },
        risksAndCatalysts: {
          type: 'object',
          properties: {
            catalysts: {
              type: 'array',
              items: { type: 'string' },
            },
            risks: {
              type: 'array',
              items: { type: 'string' },
            },
          },
          required: ['catalysts', 'risks'],
        },
        lastUpdated: { type: 'string' },
      },
      required: [
        'companyOverview',
        'marketStatus',
        'fundamentalAnalysis',
        'technicalAnalysis',
        'marketSentiment',
        'recentDevelopments',
        'investmentOutlook',
        'risksAndCatalysts',
        'lastUpdated',
      ],
    },
  },
  required: ['comprehensive_analysis'],
};
